import { Column, Index } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';

/**
 * One row per LLM task call made through `AnthropicTaskService` — the single
 * choke point every product-matching / email-ingest model call passes through.
 *
 * This is a per-call LEDGER of facts, deliberately not a bill:
 *
 *   - **Tokens are stored, dollars are not.** A token count is a fact the API
 *     reported and it never changes; a price is a decision that changes without
 *     warning and differs per contract. Multiplying at write time would freeze
 *     yesterday's rate into the row and make a re-priced month unrecoverable.
 *     Costing is a reporting-time join against whatever the rate card says.
 *   - **No rollups here.** Monthly / per-feature / per-tenant totals are a
 *     `SUM(...) GROUP BY` over these rows. Materialising them would add a second
 *     thing that can disagree with the ledger, and the ledger is the one that is
 *     right.
 *   - **The conversation-agents usage stack (`usage-tracking.service.ts`) is a
 *     deliberate NON-precedent.** That stack stores agent-keyed AGGREGATES with
 *     no input/output split, which answers "how much has this agent used" and
 *     cannot answer "what did this extraction cost" or "which request burned the
 *     web-search budget". Both may exist; neither should be rebuilt as the other.
 *
 * `auditable: false` — this is high-volume telemetry written on every model
 * call. An `_audit` row per insert would exactly double the write volume to
 * record that a telemetry row was inserted, which is information the row itself
 * already carries. Same reasoning as `license_pipeline_run`.
 */
@Index('idx_llm_task_usage_feature', ['feature'])
@Index('idx_llm_task_usage_reference', ['referenceType', 'referenceId'])
@Entity('llm_task_usage', {}, { auditable: false })
export class LlmTaskUsageEntity extends BaseEntity {
	/**
	 * Which part of the product-matching pipeline made the call, e.g.
	 * `email_ingest` or `external_lookup`. A free-form string rather than an enum
	 * column so a new caller is one constant at the call site; the values are
	 * grouped on at report time.
	 */
	@Column('varchar', { name: 'feature', length: 64 })
	public feature!: string;

	/**
	 * The task label the caller already passes to `AnthropicTaskService` — the
	 * forced output tool's name, or a more specific per-chunk label such as
	 * `order_sheet:invoice.pdf`. It is what makes two calls under the same
	 * feature distinguishable.
	 */
	@Column('varchar', { name: 'label', length: 128 })
	public label!: string;

	/** The model the call actually ran on, after the service resolved defaults. */
	@Column('varchar', { name: 'model', length: 64 })
	public model!: string;

	@Column('int', { name: 'inputTokens', unsigned: true })
	public inputTokens!: number;

	@Column('int', { name: 'outputTokens', unsigned: true })
	public outputTokens!: number;

	/**
	 * Prompt-cache tokens, kept SEPARATE from `inputTokens` because they are
	 * billed at different rates than plain input — a cache write costs more than
	 * an ordinary input token and a cache read costs far less. Folding them into
	 * one number would make the row unpriceable. Null when the API did not report
	 * them (the field is nullable on the SDK `Usage` type).
	 */
	@Column('int', {
		name: 'cacheCreationInputTokens',
		unsigned: true,
		nullable: true,
	})
	public cacheCreationInputTokens!: number | null;

	@Column('int', {
		name: 'cacheReadInputTokens',
		unsigned: true,
		nullable: true,
	})
	public cacheReadInputTokens!: number | null;

	/**
	 * Hosted web-search requests the call made (`Usage.server_tool_use`). Hosted
	 * search is billed PER REQUEST on top of the tokens it produces, so a row
	 * without this cannot be costed for the external-lookup path. Null on every
	 * call that had no server-side tools enabled.
	 */
	@Column('int', { name: 'webSearchRequests', unsigned: true, nullable: true })
	public webSearchRequests!: number | null;

	/** Wall clock for the API call itself, excluding recording. */
	@Column('int', { name: 'durationMs', unsigned: true, nullable: true })
	public durationMs!: number | null;

	@Column('varchar', { name: 'stopReason', length: 32, nullable: true })
	public stopReason!: string | null;

	/**
	 * Generic provenance: what the call was FOR, e.g. `cross_reference_request` +
	 * the request id, or `email_record` + the record id.
	 *
	 * Deliberately NOT real foreign key columns, for two reasons:
	 *   1. The referent TYPE varies by feature, and a nullable FK per possible
	 *      referent would grow a column every time a caller is added.
	 *   2. Usage must survive the referent. Deleting a cross-reference request
	 *      does not un-spend its tokens, and a cascade or a RESTRICT would either
	 *      erase the cost record or block an ordinary cleanup.
	 * The consequence is accepted: a reference may dangle, and a report that
	 * joins through it must tolerate a missing row.
	 */
	@Column('varchar', { name: 'referenceType', length: 64, nullable: true })
	public referenceType!: string | null;

	@Column('int', { name: 'referenceId', nullable: true })
	public referenceId!: number | null;
}
